import {
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { erc20Abi } from 'abitype/abis'
import { usePostHog } from 'posthog-js/react'
import {
  useAccount,
  usePublicClient,
  useSwitchChain,
  useWriteContract,
} from 'wagmi'

import { active_chain } from '@repo/common/blockchain/config'

import { FissionFundMinterAbi } from '../blockchain/abis/FissionFundMinter'
import { PriceManagerAbi } from '../blockchain/abis/PriceManager'
import { useCreateAndSignPermit2 } from '../blockchain/useCreateAndSignPermit2'
import {
  amount_to_tokens,
  encoded_to_percentage,
  tokens_to_amount,
} from '../helpers/block_helpers'
import { token_balance_change } from '../helpers/invalidator_helpers'
import { reportEnableFailed } from '../helpers/utilities'
import { useGetSymbolToDecimal } from '../portfolio'
import { type FundSymbol, useGetTokenAddress } from '../products'
import { keys as token_auth_keys } from '../token_auth'

import { keys, useActiveFundInfo } from './funds_queries'

export function useGetMinterLimitLeft({ symbol }: { symbol: FundSymbol }) {
  const { data: fund } = useActiveFundInfo({ symbol })
  const minter_address = fund?.minter_address
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    minter_address != null && client != null && symbol_to_decimal.isSuccess

  reportEnableFailed({
    name: 'useGetMinterLimitLeft',
    parts: {
      client,
      minter_address,
      symbol_to_decimal,
    },
  })

  return useQuery<number>({
    queryKey: keys.funds.details({ symbol })._ctx.deposit_limit.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const results = await client.multicall({
            contracts: [
              {
                abi: FissionFundMinterAbi,
                address: minter_address,
                functionName: 'totalUsdcDeposited',
              },
              {
                abi: FissionFundMinterAbi,
                address: minter_address,
                functionName: 'usdcMintingCap',
              },
            ],
          })

          const deposited = results[0].result ?? 0n
          const max = results[1].result ?? 0n

          return amount_to_tokens(max - deposited, symbol_to_decimal[symbol])
        },
  })
}

export function useGetFundTotalTokens({ symbol }: { symbol: FundSymbol }) {
  const { data } = useActiveFundInfo({ symbol })
  const token_address = data?.token_address
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    token_address != null && client != null && symbol_to_decimal.isSuccess

  reportEnableFailed({
    name: 'useGetFundTotalTokens',
    parts: {
      client,
      token_address,
      symbol_to_decimal,
    },
  })

  return useQuery<number>({
    queryKey: keys.funds.details({ symbol })._ctx.total_tokens.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const amount = await client.readContract({
            abi: erc20Abi,
            functionName: 'totalSupply',
            address: token_address,
          })
          return amount_to_tokens(amount, symbol_to_decimal[symbol])
        },
  })
}

export function useGetMinterPaused({ symbol }: { symbol: FundSymbol }) {
  const { data: fund } = useActiveFundInfo({ symbol })
  const minter_address = fund?.minter_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = minter_address != null && client != null

  reportEnableFailed({
    name: 'useGetMinterPaused',
    parts: {
      client,
      minter_address,
    },
  })

  return useQuery<boolean>({
    queryKey: keys.funds.details({ symbol })._ctx.minter_paused.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          return await client.readContract({
            abi: FissionFundMinterAbi,
            address: minter_address,
            functionName: 'paused',
          })
        },
  })
}

export function useGetMinterPriceSafety({ symbol }: { symbol: FundSymbol }) {
  const { data: fund } = useActiveFundInfo({ symbol })
  const minter_address = fund?.minter_address
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled = minter_address != null && client != null

  reportEnableFailed({
    name: 'useGetMinterPriceSafety',
    parts: {
      client,
      minter_address,
    },
  })

  return useQuery<{ price_safety_percentage: number }>({
    queryKey: keys.funds.details({ symbol })._ctx.minter_settings.queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          const encoded = await client.readContract({
            abi: FissionFundMinterAbi,
            address: minter_address,
            functionName: 'priceSafetyPercentage',
          })

          return { price_safety_percentage: encoded_to_percentage(encoded) }
        },
  })
}

export function useGetPriceManager({ symbol }: { symbol: FundSymbol }) {
  const { data: fund } = useActiveFundInfo({ symbol })
  const minter_address = fund?.minter_address
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    minter_address != null && client != null && symbol_to_decimal != null

  reportEnableFailed({
    name: 'useGetPriceManager',
    parts: {
      client,
      minter_address,
      symbol_to_decimal,
    },
  })

  return useQuery<CryptoAddress>({
    queryKey: keys.funds.details({ symbol })._ctx.price_manager_address
      .queryKey,
    queryFn: !enabled
      ? skipToken
      : async () => {
          return await client.readContract({
            abi: FissionFundMinterAbi,
            functionName: 'priceManager',
            address: minter_address,
          })
        },
  })
}

export type fund_prices_type = {
  dex: number
  observation: number
  mint_floor: number
} & (
  | {
      oracle_outdated: true
      oracle: null
      mint: null
    }
  | {
      oracle_outdated: false
      oracle: number
      mint: number
    }
)

// change to get all the prices at once: Oracle, Spot, Observation, Mint, MintFloor
// eslint-disable-next-line complexity, max-lines-per-function
export function useGetFundPrices({ symbol }: { symbol: FundSymbol }) {
  const fund_query = useActiveFundInfo({ symbol })
  const minter_address = fund_query.data?.minter_address
  const token_address = fund_query.data?.token_address
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })
  const { data: price_manager_address } = useGetPriceManager({ symbol })
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })

  const enabled =
    minter_address != null &&
    token_address != null &&
    usdc_address != null &&
    price_manager_address != null &&
    client != null &&
    symbol_to_decimal.isSuccess

  return useQuery<fund_prices_type>({
    queryKey: keys.funds.details({ symbol })._ctx.prices.queryKey,
    queryFn: !enabled
      ? skipToken
      : // eslint-disable-next-line complexity
        async () => {
          const results = await client.multicall({
            contracts: [
              {
                abi: PriceManagerAbi,
                address: price_manager_address,
                functionName: 'getPriceFeedTokenPrices',
                args: [token_address],
              },
              {
                abi: PriceManagerAbi,
                address: price_manager_address,
                functionName: 'getUniswapV3SpotPrice',
                args: [token_address, usdc_address],
              },
              {
                abi: PriceManagerAbi,
                address: price_manager_address,
                functionName: 'getUniswapV3ObservationPrice',
                args: [token_address, usdc_address],
              },
              {
                abi: FissionFundMinterAbi,
                address: minter_address,
                functionName: 'getMintPrice',
              },
              {
                abi: FissionFundMinterAbi,
                address: minter_address,
                functionName: 'mintFloorPrice',
              },
            ],
          })

          const usdc_decimals = symbol_to_decimal['USDC']

          const dex = amount_to_tokens(results[1].result ?? 0n, usdc_decimals)
          const observation = amount_to_tokens(
            results[2].result ?? 0n,
            usdc_decimals,
          )
          const mint_floor = amount_to_tokens(
            results[4].result ?? 0n,
            usdc_decimals,
          )

          if (results[0].error != null || results[3].error) {
            console.error(results[0].error)
            // this is the only known reason for an error
            return {
              oracle_outdated: true,
              oracle: null,
              dex,
              observation,
              mint: null,
              mint_floor,
            }
          }

          return {
            oracle_outdated: false,
            oracle: amount_to_tokens(
              results[0].result[1],
              results[0].result[0],
            ),
            dex,
            observation,
            mint: amount_to_tokens(results[3].result, usdc_decimals),
            mint_floor,
          }
        },
  })
}

// eslint-disable-next-line complexity, max-lines-per-function
export function useBuyFund({ symbol }: { symbol: FundSymbol }) {
  const queryClient = useQueryClient()
  const { writeContractAsync } = useWriteContract()
  const { switchChainAsync } = useSwitchChain()
  const { data: fund } = useActiveFundInfo({ symbol })
  const { data: prices } = useGetFundPrices({ symbol })
  const { address: wallet_address } = useAccount()
  const symbol_to_decimal = useGetSymbolToDecimal()
  const client = usePublicClient({ chainId: active_chain.id })
  const posthog = usePostHog()
  const { createSingleAsync, isReady: permit_is_ready } =
    useCreateAndSignPermit2()

  const minter_address = fund?.minter_address
  const usdc_address = useGetTokenAddress({ symbol: 'USDC' })

  const isReady =
    permit_is_ready &&
    wallet_address != null &&
    fund != null &&
    prices != null &&
    symbol_to_decimal.isSuccess &&
    usdc_address != null &&
    minter_address != null &&
    client != null

  const result = useMutation({
    mutationFn: async ({
      tokens_in,
      tokens_out,
      slippage_percentage,
    }: {
      tokens_in: number
      tokens_out: number
      slippage_percentage: number
    }) => {
      if (!isReady) {
        console.warn('fund mint called before ready')
        return
      }

      const usdc_amount = tokens_to_amount(tokens_in, symbol_to_decimal['USDC'])
      const min_fund_amount = tokens_to_amount(
        (tokens_out * (100 - slippage_percentage)) / 100,
        symbol_to_decimal[symbol],
      )

      // ensure network is correct
      await switchChainAsync({ chainId: active_chain.id })

      const { permitSingle, signature } = await createSingleAsync({
        token_address: usdc_address,
        token_symbol: 'USDC',
        destination_address: minter_address,
        token_amount: usdc_amount,
      })

      const tx = await writeContractAsync({
        abi: FissionFundMinterAbi,
        address: minter_address,
        functionName: 'mintWithPermit2',
        args: [
          permitSingle,
          signature,
          usdc_amount,
          wallet_address,
          min_fund_amount,
        ],
        chainId: active_chain.id,
      })

      await client.waitForTransactionReceipt({ hash: tx })
      posthog.capture('fund minted', {
        fund: symbol,
        usdc_tokens: tokens_in,
      })
    },
    onSuccess: () => {
      void token_balance_change({
        queryClient,
        token_address: fund!.token_address,
        wallet_address: wallet_address!,
      })

      void queryClient.invalidateQueries({
        queryKey: token_auth_keys.token_auth.sdd_purchase_stats({
          symbol,
          wallet_address: wallet_address!,
        }).queryKey,
      })
    },
  })
  return {
    ...result,
    isReady,
  }
}
